import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2, HeartHandshake, Sparkles, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VOLUNTEER_INTERESTS = [
  'Language for Juniors (L4J)',
  'Civil Society Intervention (CSI)',
  'Skill Training Initiative (STI)',
  'Volunteer Induction Programme (VIP)',
  'Counselling',
  'Research',
  'Medical Aid & Welfare',
  'Fundraising / Events',
];

const AVAILABILITY = ['Weekdays', 'Weekends', 'Evenings only', 'Flexible'];

/**
 * Shared reusable enquiry modal for both "Book Counselling" and "Volunteer"
 * flows. Same look, only the fields shown vary by `type`.
 *
 * Props:
 *   open, onOpenChange:   Dialog control
 *   type: 'counselling' | 'volunteer'
 */
const EnquiryDialog = ({ open, onOpenChange, type }) => {
  const isCounselling = type === 'counselling';
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    concern: '', preferred_date: '', preferred_time: '',
    age_group: '', availability: '', interests: [],
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (label) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(label)
        ? f.interests.filter((i) => i !== label)
        : [...f.interests, label],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.trim().length < 2) {
      toast({ title: 'Missing name', description: 'Please tell us your name.', variant: 'destructive' });
      return;
    }
    if (!form.phone || form.phone.trim().length < 6) {
      toast({ title: 'Missing phone', description: 'Please share a phone/WhatsApp number.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { type, ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${BACKEND_URL}/api/enquiries`, payload);
      setSubmitted(true);
      toast({
        title: isCounselling ? 'Counselling request received' : 'Thanks for volunteering!',
        description: 'Our team will reach out within 24 hours.',
      });
    } catch (err) {
      toast({ title: 'Submission failed', description: 'Please try WhatsApp instead.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange?.(false);
    // reset a beat later so the closing animation isn't jarring
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: '', phone: '', email: '', concern: '', preferred_date: '', preferred_time: '',
                age_group: '', availability: '', interests: [], message: '' });
    }, 300);
  };

  const Icon = isCounselling ? HeartHandshake : Sparkles;
  const title = isCounselling ? 'Book a Counselling Session' : 'Join EWRF as a Volunteer';
  const subtitle = isCounselling
    ? 'Free · Confidential · Trained volunteer counsellors — usually within 24 hours.'
    : '500+ active volunteers across 8 chapters. Pick what excites you.';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid={`enquiry-dialog-${type}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Icon className="w-5 h-5 text-[#1565C0]" />
            {title}
          </DialogTitle>
          <p className="text-sm text-gray-500 -mt-1">{subtitle}</p>
        </DialogHeader>

        {submitted ? (
          <div className="py-8 text-center" data-testid="enquiry-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <div className="text-lg font-semibold text-gray-900">You&apos;re all set!</div>
            <p className="text-sm text-gray-600 mt-1 max-w-sm mx-auto">
              We received your details. A team member will reach out on WhatsApp within 24 hours.
            </p>
            <Button className="mt-6" onClick={close} data-testid="enquiry-done-btn">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="enq-name">Full name *</Label>
                <Input
                  id="enq-name" value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Your name"
                  data-testid="enquiry-name"
                />
              </div>
              <div>
                <Label htmlFor="enq-phone">Phone / WhatsApp *</Label>
                <Input
                  id="enq-phone" value={form.phone} type="tel"
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="012-345 6789"
                  data-testid="enquiry-phone"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="enq-email">Email (optional)</Label>
              <Input
                id="enq-email" value={form.email} type="email"
                onChange={(e) => set('email', e.target.value)}
                placeholder="you@example.com"
                data-testid="enquiry-email"
              />
            </div>

            {isCounselling ? (
              <>
                <div>
                  <Label htmlFor="enq-concern">What would you like to talk about?</Label>
                  <Input
                    id="enq-concern" value={form.concern}
                    onChange={(e) => set('concern', e.target.value)}
                    placeholder="Anxiety, family issues, exam stress…"
                    data-testid="enquiry-concern"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="enq-date">Preferred date</Label>
                    <Input
                      id="enq-date" type="date" value={form.preferred_date}
                      onChange={(e) => set('preferred_date', e.target.value)}
                      data-testid="enquiry-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="enq-time">Preferred time</Label>
                    <Input
                      id="enq-time" value={form.preferred_time}
                      onChange={(e) => set('preferred_time', e.target.value)}
                      placeholder="e.g. 10am–12pm"
                      data-testid="enquiry-time"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Interests <span className="text-xs text-gray-400">(tap to toggle)</span></Label>
                  <div className="flex flex-wrap gap-2 mt-1" data-testid="enquiry-interests">
                    {VOLUNTEER_INTERESTS.map((i) => {
                      const active = form.interests.includes(i);
                      return (
                        <button
                          type="button"
                          key={i}
                          onClick={() => toggleInterest(i)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            active
                              ? 'bg-[#1565C0] border-[#1565C0] text-white'
                              : 'border-gray-300 text-gray-700 hover:border-[#1565C0]'
                          }`}
                          data-testid={`enquiry-interest-${i.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
                        >
                          {i}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="enq-age">Age group</Label>
                    <Input
                      id="enq-age" value={form.age_group}
                      onChange={(e) => set('age_group', e.target.value)}
                      placeholder="e.g. 20-30"
                      data-testid="enquiry-age"
                    />
                  </div>
                  <div>
                    <Label htmlFor="enq-avail">Availability</Label>
                    <select
                      id="enq-avail"
                      value={form.availability}
                      onChange={(e) => set('availability', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="enquiry-availability"
                    >
                      <option value="">Select…</option>
                      {AVAILABILITY.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="enq-msg">Anything else? (optional)</Label>
              <Textarea
                id="enq-msg" rows={3} value={form.message}
                onChange={(e) => set('message', e.target.value)}
                placeholder={isCounselling ? 'Any context that would help us prepare…' : 'Share your motivation, skills, or questions.'}
                data-testid="enquiry-message"
              />
            </div>

            <div className="pt-1 flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#1565C0] hover:bg-[#0F4C9E]"
                data-testid="enquiry-submit"
              >
                <Send className="w-4 h-4 mr-1" />
                {submitting ? 'Sending…' : (isCounselling ? 'Send Booking' : 'Sign Me Up')}
              </Button>
            </div>
            <p className="text-[11px] text-gray-400 leading-4">
              Your information stays private — used only to reach out about {isCounselling ? 'your session' : 'volunteer placements'}.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryDialog;
